import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Calendar, Notebook, ChatCircleText, ChartBar, Sparkle, Fire, Target, Clock, ArrowRight, Compass, X } from "@phosphor-icons/react";

const ANAM_NUDGE_KEY = "caminhos_anam_nudge_dismissed_v1";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [agenda, setAgenda] = useState([]);
  const [perf, setPerf] = useState([]);
  const [hasAnam, setHasAnam] = useState(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(() => localStorage.getItem(ANAM_NUDGE_KEY) === "1");

  useEffect(() => {
    api.get("/agenda").then(r => setAgenda(r.data)).catch(() => {});
    api.get("/performance").then(r => setPerf(r.data)).catch(() => {});
    api.get("/anamnesis").then(r => setHasAnam((r.data || []).length > 0)).catch(() => setHasAnam(false));
  }, []);

  function dismissNudge() { localStorage.setItem(ANAM_NUDGE_KEY, "1"); setNudgeDismissed(true); }

  const upcoming = agenda.filter(a => !a.completed).slice(0, 5);
  const avg = perf.length ? (perf.reduce((s, p) => s + (p.score / (p.max_score || 100)) * 100, 0) / perf.length).toFixed(0) : "—";
  const completed = agenda.filter(a => a.completed).length;
  const totalMinutes = agenda.filter(a => a.completed).reduce((s, a) => s + (a.duration_min || 0), 0);
  const hoursStudied = (totalMinutes / 60).toFixed(1);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";
  const firstName = user?.name?.split(" ")[0];

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-7xl space-y-6 sm:space-y-8">
      <header className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="pill-badge bg-white text-[#1E5FFF] border border-[#1E5FFF]/20 mb-4">
            <Sparkle size={12} weight="fill" /> Painel do aluno
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-[#12143A] leading-tight">
            {greeting}, <span className="text-[#1E5FFF]">{firstName}</span>! 👋
          </h1>
          <p className="text-zinc-500 mt-2 max-w-xl text-sm sm:text-base">Sua jornada de estudos guiada pelos princípios de <em>Os Caminhos para Aprovação</em>.</p>
        </div>
      </header>

      {hasAnam === false && !nudgeDismissed && (
        <div data-testid="anam-nudge" className="fade-up relative overflow-hidden rounded-3xl border border-[#1E5FFF]/20 shadow-[0_10px_30px_rgba(30,95,255,0.12)]"
             style={{animationDelay: '0.03s'}}>
          <div className="absolute inset-0 bg-gradient-to-br from-[#F4F8FF] via-white to-[#FFF6EA]/60" />
          <div className="absolute -right-6 -top-6 w-40 h-40 rounded-full bg-[#1E5FFF]/10 blur-2xl" />
          <div className="absolute -left-8 -bottom-8 w-40 h-40 rounded-full bg-[#FFB86B]/20 blur-2xl" />
          <button onClick={dismissNudge} data-testid="anam-nudge-dismiss"
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-zinc-500 z-10" aria-label="Dispensar">
            <X size={14} weight="bold" />
          </button>
          <div className="relative p-5 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center flex-shrink-0 shadow-md">
              <Compass size={26} color="white" weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1E5FFF]">Boas-vindas</div>
              <h3 className="font-heading text-xl sm:text-2xl font-black text-[#12143A] leading-tight mt-1">Antes de começar, vamos conhecer seu jeito de estudar?</h3>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed max-w-xl">
                Responda 15 perguntas rápidas e receba um <b>diagnóstico personalizado</b> com pontos fortes, gargalos e um plano de 7 dias — feito especialmente para você.
              </p>
            </div>
            <Link to="/anamnesis" data-testid="anam-nudge-cta"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full gradient-primary text-white font-bold text-sm shadow-[0_8px_20px_rgba(30,95,255,0.35)] hover:-translate-y-0.5 transition-transform whitespace-nowrap">
              <Sparkle size={14} weight="fill" /> Começar avaliação <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5 fade-up" style={{animationDelay: '0.05s'}}>
        <StatCard icon={Target} tint="#1E5FFF" bg="gradient-sky" label="Média geral" value={avg + (avg !== "—" ? "%" : "")} sub="rendimento acumulado" testid="stat-avg" />
        <StatCard icon={Fire} tint="#FF3D71" bg="gradient-blush" label="Concluído" value={String(completed)} sub={`de ${agenda.length} atividades`} testid="stat-pending" />
        <StatCard icon={Clock} tint="#12143A" bg="bg-[#FFF6EA]" label="Horas líquidas" value={hoursStudied + "h"} sub="tempo em foco" testid="stat-perf" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Link to="/tutor" data-testid="card-tutor"
              className="tracing-beam p-7 lg:col-span-2 fade-up group hover:-translate-y-1"
              style={{animationDelay: '0.1s'}}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="pill-badge bg-[#1E5FFF]/10 text-[#1E5FFF] mb-3"><Sparkle size={11} weight="fill" /> Tutor IA</div>
              <h3 className="font-heading text-3xl font-black text-[#12143A]">Tire dúvidas com contexto do livro</h3>
              <p className="text-sm text-zinc-600 mt-2 max-w-md leading-relaxed">
                Pergunte sobre técnicas, revisão espaçada, ENEM, matérias... Respostas didáticas baseadas em <em>Os Caminhos para Aprovação</em>.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#1E5FFF] group-hover:gap-3 transition-all">
                Abrir tutor <ArrowRight size={16} weight="bold" />
              </div>
            </div>
            <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center shadow-lg flex-shrink-0 group-hover:rotate-6 transition-transform">
              <Sparkle size={28} color="white" weight="fill" />
            </div>
          </div>
        </Link>

        <Link to="/plan" data-testid="card-plan"
              className="card-soft p-7 fade-up hover:-translate-y-1 group"
              style={{animationDelay: '0.15s'}}>
          <div className="w-14 h-14 rounded-2xl gradient-warm flex items-center justify-center shadow-md mb-4 group-hover:scale-110 transition-transform">
            <Notebook size={26} color="white" weight="duotone" />
          </div>
          <h3 className="font-heading text-xl font-black text-[#12143A]">Plano de estudos IA</h3>
          <p className="text-sm text-zinc-500 mt-2 leading-relaxed">Gere seu plano semanal personalizado em segundos.</p>
          <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#FF3D71] group-hover:gap-2 transition-all">
            Criar plano <ArrowRight size={14} weight="bold" />
          </div>
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="card-soft p-6 fade-up" style={{animationDelay: '0.2s'}}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-xl font-black flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-[#E0EBFF] flex items-center justify-center">
                <Calendar size={18} weight="duotone" color="#1E5FFF" />
              </div>
              Próximas atividades
            </h3>
            <Link to="/agenda" className="text-xs font-bold uppercase tracking-wider text-[#1E5FFF] hover:underline">Ver tudo</Link>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🎉</div>
              <p className="text-sm text-zinc-500">Sem pendências. Aproveite para revisar!</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {upcoming.map(a => (
                <li key={a.id} className="flex items-center gap-4 p-3 rounded-2xl hover:bg-zinc-50 transition-colors">
                  <div className="w-11 h-11 rounded-2xl gradient-sky flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-[#1E5FFF] uppercase">{(a.date || "").slice(5).replace("-", "/")}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{a.title}</div>
                    <div className="text-xs text-zinc-500">{a.subject} • {a.duration_min} min</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-soft p-6 fade-up" style={{animationDelay: '0.25s'}}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading text-xl font-black flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-[#FFE9EE] flex items-center justify-center">
                <ChartBar size={18} weight="duotone" color="#FF3D71" />
              </div>
              Últimas notas
            </h3>
            <Link to="/performance" className="text-xs font-bold uppercase tracking-wider text-[#1E5FFF] hover:underline">Ver tudo</Link>
          </div>
          {perf.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">📚</div>
              <p className="text-sm text-zinc-500">Nenhuma nota registrada ainda.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {perf.slice(0, 5).map(p => {
                const pct = (p.score / (p.max_score || 100)) * 100;
                const color = pct >= 70 ? "#12B76A" : pct >= 50 ? "#F79009" : "#F04438";
                return (
                  <li key={p.id} className="flex items-center gap-4 p-3 rounded-2xl hover:bg-zinc-50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{p.subject}</div>
                      <div className="text-xs text-zinc-500">{p.date}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-num text-xl font-black" style={{color}}>{p.score}<span className="text-sm text-zinc-400">/{p.max_score}</span></div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, tint, bg, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className={`card-soft p-6 relative overflow-hidden`}>
      <div className={`absolute inset-0 opacity-40 ${bg}`} />
      <div className="relative">
        <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center shadow-sm mb-3">
          <Icon size={20} weight="duotone" color={tint} />
        </div>
        <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-500">{label}</div>
        <div className="font-mono-num text-4xl font-black mt-1 text-[#12143A]">{value}</div>
        <div className="text-xs text-zinc-500 mt-1">{sub}</div>
      </div>
    </div>
  );
}
